import { useEffect, useState } from "react";

import { Button, Checkbox, CheckboxGroup, Divider, Flex, FormControl, FormErrorMessage, FormLabel, Input, Select, Switch, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST, ROLES, VESSEL_STATUS } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { editVesselShipOwnerSchema } from "@/schemas/editVesselShipOwnerSchema";
import { getVesselById, postVessel, putVessel } from "@/services/vesselService";
import EditFile from "@/components/Fields/EditFile";
import { getShipOwnerById, getListShipOwner, updateOnBehalfCompany } from "@/services/shipOwnerService";
import * as Yup from 'yup';

const EditVesselShipOwner = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const appId = useAppSelector((state: RootState) => state.account.appId);
    const name = useAppSelector((state: RootState) => state.account.name);

    const { id, onBehalfCompany, idShipOwner } = location.state || {};

    const [dataEdit, setDataEdit] = useState<any>(null)
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmButtonText, setConfirmButtonText] = useState("");
    const [cancelButtonText, setCancelButtonText] = useState("");
    const [dataDetail, setDataDetail] = useState<any>(null);
    const [defaultType, setDefaultType] = useState<string[]>([]);
    const [defaultOnBehalfCompany, setDefaultOnBehalfCompany] = useState("");
    const [shipOwners, setShipOwners] = useState<any[]>([]);
    const [isLoadingOwners, setIsLoadingOwners] = useState(false);
    const [ownersError, setOwnersError] = useState("");

    const handleCancel = () => {
        if (dataDetail?.status === 2 && role === 'ship operator') {
            navigate('/manage-vessel/');
            return;
        }

        onOpen();
        setTitle("Cancel Changes?");
        setConfirmationTest("Are you sure you want to discard all changes?");
        setConfirmButtonText("Yes");
        setCancelButtonText("No");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Save Vessel Data?");
        setConfirmationTest("Are you sure you want to save?");
        setConfirmButtonText("Save");
        setCancelButtonText("Cancel");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateDataEdit = () => {
        const data = new FormData();
        if (id){
            data.append('VesselId', id);
        }
        data.append('ShipOwnerId', dataEdit.shipOwnerId ?? idShipOwner ?? appId);
        data.append('IMOREG', dataEdit.imoRegNumber.toString());
        data.append('VesselName', dataEdit.vesselName.trim());
        data.append('Sirebire', dataEdit.type.includes('SIRE | BIRE'));
        data.append('Vetting', dataEdit.type.includes('Vetting'));
        data.append('PicName', dataEdit.PicName ?? '');
        data.append('PicEmail', dataEdit.PicEmail ?? '');
        // data.append('RegisterStatus', role === 'super admin' ? '3' : '1');

        if (!id){
            data.append('RegisterStatus', role === ROLES.SUPER_ADMIN ? String(VESSEL_STATUS.ACTIVE) : String(VESSEL_STATUS.NEW));
        } else if (dataDetail?.status == VESSEL_STATUS.REJECTED) {
            data.append('RegisterStatus', String(VESSEL_STATUS.IN_REVIEW));
        } else {
            data.append('RegisterStatus', dataEdit.status ? String(VESSEL_STATUS.ACTIVE) : String(VESSEL_STATUS.INACTIVE));
        }

        data.append('SMCFile', dataEdit.smc ?? '');
        // data.append('Status', dataEdit.status);
        data.append('ModifiedBy', name);
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title.startsWith("Cancel")) {
            /*if (role === 'super admin') {
                navigate('/manage-ship-operator/vessel', { state: { idShipOwner } })
            } else {
                navigate('/manage-vessel/');
            }*/
            history.back();
            return;
        }

        const obj = generateDataEdit();
        let response;
        if (id) {
            // Only call updateOnBehalfCompany if the value has changed
            if (dataEdit.onBehalfCompany !== defaultOnBehalfCompany) {
                const responses = await Promise.all([
                    putVessel(obj),
                    updateOnBehalfCompany({
                        shipOwnerId: dataEdit.shipOwnerId,
                        onBehalfCompany: dataEdit.onBehalfCompany
                    })
                ]);
                const putVesselResponse: any = responses[0];
                const updateOnBehalfCompanyResponse: any = responses[1];
                if (putVesselResponse.status !== HttpStatusCode.Ok || updateOnBehalfCompanyResponse.status !== HttpStatusCode.Ok) {
                    toast({
                        title: putVesselResponse.status !== HttpStatusCode.Ok ? (putVesselResponse.status ?? 'Vessel failed to updated.') : (updateOnBehalfCompanyResponse.status ?? 'Company name update failed.'),
                        description: putVesselResponse.status !== HttpStatusCode.Ok ? (putVesselResponse.message ?? "We could not save this vessel data for you.") : (updateOnBehalfCompanyResponse.message ?? "We could not update the company name for you."),
                        status: 'error',
                        duration: DURATION_TOAST,
                        isClosable: true,
                    });
                    setIsSubmitting(false);
                    return;
                }
                response = putVesselResponse;
            } else {
                response = await putVessel(obj);
            }
        } else {
            response = await postVessel(obj);
        }
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Vessel updated.',
                description: "We've updated the vessel data for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setTimeout(() => {
                /*if (role === 'super admin') {
                    navigate('/manage-ship-operator/vessel', { state: { idShipOwner } })
                } else {
                    navigate('/manage-vessel');
                }*/
                history.back();
            }, 1000)
        } else {
            toast({
                title: response.status ?? 'Vessel failed to updated.',
                description: response.message ?? "We could not save this vessel data for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const getDataDetail = async () => {
        const response = await getVesselById(id) as any;
        if (response) {
            setDataDetail(response.data);
            const newDefaultType: string[] = [];

            if (response.data.sirebire) {
                newDefaultType.push('SIRE | BIRE');
            }

            if (response.data.vetting) {
                newDefaultType.push('Vetting');
            }

            setDefaultType(newDefaultType);
        }
    }

    const getOperatorDetail = async () => {
        const response = await getShipOwnerById(idShipOwner ?? appId) as any;
        if (response.status === HttpStatusCode.Ok) {
            setDefaultOnBehalfCompany(response.data.onBehalfCompany);
        }
    }

    const getShipOwnersList = async () => {
        setIsLoadingOwners(true);
        setOwnersError("");
        const params = {
            search: "",
            status: "",
            pageNumber: 1,
            pageSize: 1000,
        };
        try {
            const response = await getListShipOwner(params) as any;
            if (response.status === HttpStatusCode.Ok) {
                setShipOwners(response.data || []);
            } else {
                setOwnersError(response.error?.message || "Failed to fetch ship owners");
            }
        } catch (e: any) {
            setOwnersError(e?.message || "Failed to fetch ship owners");
        } finally {
            setIsLoadingOwners(false);
        }
    }

    useEffect(() => {
        console.log(location.state)
        if (id) {
            getDataDetail();
            getShipOwnersList();
        }
    }, [id])

    useEffect(() => {
        if (!onBehalfCompany) {
            getOperatorDetail();
        }
    }, [onBehalfCompany])

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmButtonText} cancelButtonText={cancelButtonText} />
            <div className="form-container">
                <Formik
                    initialValues={{
                        imo: dataDetail?.imo ?? '',
                        imoRegNumber: dataDetail?.imoreg ?? '',
                        vesselName: dataDetail?.vesselName ?? '',
                        type: dataDetail ? defaultType : [],
                        status: dataDetail?.status === 3,
                        smc: null,
                        onBehalfCompany: onBehalfCompany || defaultOnBehalfCompany,
                        shipOwnerId: idShipOwner || dataDetail?.shipOwnerId,
                        PicName: dataDetail?.picName || '',
                        PicEmail: dataDetail?.picEmail || '',
                    }}
                    validationSchema={
                        id
                            ? editVesselShipOwnerSchema.concat(
                                Yup.object({
                                    shipOwnerId: Yup.string().required('Ship owner is required'),
                                    PicEmail: Yup.string()
                                        .email('Invalid email address')
                                        .nullable(),
                                })
                            )
                            : editVesselShipOwnerSchema.concat(
                                Yup.object({
                                    PicEmail: Yup.string()
                                        .email('Invalid email address')
                                        .nullable(),
                                })
                            )
                    }
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form>
                            {id && (
                                <Field name='shipOwnerId'>
                                    {({ form }: any) => (
                                        <FormControl isInvalid={(form.errors.shipOwnerId && form.touched.shipOwnerId) || ownersError} marginBottom={4}>
                                            <div className="form-field-container">
                                                <FormLabel htmlFor="shipOwnerId" className="form-label">Ship Owner<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <Select
                                                        id="shipOwnerId"
                                                        placeholder={isLoadingOwners ? 'Loading...' : 'Select Ship Owner'}
                                                        value={props.values.shipOwnerId}
                                                        onChange={(e: any) => props.setFieldValue('shipOwnerId', e.target.value)}
                                                        isDisabled={isLoadingOwners}
                                                    >
                                                        {shipOwners.map((item: any) => (
                                                            <option key={item.id} value={item.id}>{`${item.onBehalfCompany} - ${item.id}`}</option>
                                                        ))}
                                                    </Select>
                                                    <FormErrorMessage>{ownersError || form.errors.shipOwnerId}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                            )}
                            <Field name='type'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.type && form.touched.type} marginBottom={4}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor="type" className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CheckboxGroup
                                                    value={props.values.type}
                                                    onChange={(value: string[]) => props.setFieldValue('type', value)}
                                                    isDisabled={dataDetail?.status === 2}
                                                >
                                                    <Flex gap={8}>
                                                        <Checkbox value="SIRE | BIRE">SIRE | BIRE</Checkbox>
                                                        <Checkbox value="Vetting">Vetting</Checkbox>
                                                    </Flex>
                                                </CheckboxGroup>
                                                <FormErrorMessage>{form.errors.type}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='imoRegNumber'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.imoRegNumber && form.touched.imoRegNumber} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">IMO / Registrasi Number<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='IMO / Registrasi Number' className="form-input" />
                                                <FormErrorMessage>{form.errors.imoRegNumber}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='vesselName'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.vesselName && form.touched.vesselName} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Vessel Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Vessel Name' className="form-input" isDisabled={dataDetail?.status === 2} />
                                                <FormErrorMessage>{form.errors.vesselName}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {/* Company Name (onBehalfCompany) field, not mandatory */}
                            {   id && 
                                (<Field name='onBehalfCompany'>
                                    {({ field }: any) => (
                                        <FormControl style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Company Name</FormLabel>
                                                <div className="form-input-container">
                                                    <Input {...field} placeholder='Company Name' className="form-input" />
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>)
                            }
                            {/* PIC Name Field */}
                            <Field name='PicName'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.PicName && form.touched.PicName} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">PIC Name</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='PIC Name' className="form-input" />
                                                <FormErrorMessage>{form.errors.PicName}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {/* PIC Email Field */}
                            <Field name='PicEmail'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.PicEmail && form.touched.PicEmail} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">PIC Email</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='PIC Email' className="form-input" />
                                                <FormErrorMessage>{form.errors.PicEmail}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='smc'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.smc && form.submitCount} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">SMC</FormLabel>
                                            <div className="form-input-container">
                                                <EditFile
                                                    fileId={dataDetail?.smc}
                                                    type="registration-inspectingcompany"
                                                    title="SMC Fie"
                                                    handleDrop={(e) => props.setFieldValue('smc', e[0])}
                                                    isInvalid={Boolean(form.errors.smc && form.submitCount)}
                                                    isDisabled={props.isSubmitting}
                                                />
                                                <FormErrorMessage>{form.errors.smc}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {/* {role === 'super admin' && <Field name='status'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.status && form.touched.status} style={{ marginBottom: 15, alignItems: 'center' }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='status-switch' className="form-label">
                                                Status
                                            </FormLabel>
                                            <Switch {...field} isChecked={props.values.status} onChange={() => props.setFieldValue('status', !props.values.status)} id='status-switch' style={{ width: '50%', marginTop: '5px', backgroundColor: 'white' }} />
                                        </div>
                                    </FormControl>
                                )}
                            </Field>} */}
                            {id && (<Field name='status'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.status && form.touched.status} style={{ marginBottom: 15, alignItems: 'center' }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='status-switch' className="form-label">
                                                Status
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <Switch {...field} isChecked={props.values.status} onChange={() => props.setFieldValue('status', !props.values.status)} id='status-switch' style={{ width: '50%', marginTop: '5px', backgroundColor: 'white' }} />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                                </Field>)
                            }
                            {/* {id ? <Field name='status'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.status && form.touched.status} style={{ marginBottom: 15, alignItems: 'center' }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='status-switch' className="form-label">
                                                Status
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <Switch {...field} isChecked={props.values.status} onChange={() => props.setFieldValue('status', !props.values.status)} id='status-switch' style={{ width: '50%', marginTop: '5px', backgroundColor: 'white' }} />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            :
                            <Field name='onBehalfCompany'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.onBehalfCompany && form.touched.onBehalfCompany} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">On Behalf Company<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} disabled placeholder='On Behalf Company' className="form-input" />
                                                <FormErrorMessage>{form.errors.onBehalfCompany}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>} */}
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor={'white'}
                                    borderColor={'#D0D5DD'}
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    {dataDetail?.status !== 2 ? 'Cancel' : 'Back'}
                                </Button>
                                {dataDetail?.status !== 2 && <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Save Vessel Data
                                </Button>}
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default EditVesselShipOwner;
